package com.evoapps.evoshift.ui.hiring

import androidx.lifecycle.compose.collectAsStateWithLifecycle
import com.evoapps.evoshift.ui.theme.animateItemIfMotion
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.lazy.grid.GridItemSpan
import androidx.compose.foundation.lazy.grid.items
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Add
import androidx.compose.material.icons.filled.Edit
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.ExtendedFloatingActionButton
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Scaffold
import androidx.compose.material3.SnackbarHost
import androidx.compose.material3.SnackbarHostState
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import com.evoapps.evoshift.domain.model.JobPost
import com.evoapps.evoshift.domain.model.JobPostStatus
import com.evoapps.evoshift.domain.model.PayUnit
import com.evoapps.evoshift.ui.Cream
import com.evoapps.evoshift.ui.DarkGold
import com.evoapps.evoshift.ui.DeepNavy
import com.evoapps.evoshift.ui.adaptive.AdaptiveCardGrid
import com.evoapps.evoshift.ui.adaptive.LocalWindowWidthClass
import com.evoapps.evoshift.ui.adaptive.contentWidth
import com.evoapps.evoshift.ui.util.asString
import com.evoapps.evoshift.viewmodels.Gate
import com.evoapps.evoshift.viewmodels.HiringViewModel
import com.evoapps.evoshift.viewmodels.shows
import com.evoapps.evoshift.widgets.EvoTopBar
import com.evoapps.evoshift.widgets.LoadingOverlay
import com.evoapps.evoshift.widgets.RegularCard
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.hiring_applicants_count
import evoshift.composeapp.generated.resources.hiring_edit
import evoshift.composeapp.generated.resources.hiring_empty
import evoshift.composeapp.generated.resources.hiring_new_post
import evoshift.composeapp.generated.resources.hiring_status_closed
import evoshift.composeapp.generated.resources.hiring_status_draft
import evoshift.composeapp.generated.resources.hiring_status_open
import evoshift.composeapp.generated.resources.hiring_title
import evoshift.composeapp.generated.resources.hiring_unread_count
import evoshift.composeapp.generated.resources.hiring_recruiter_empty
import evoshift.composeapp.generated.resources.job_pay_per_hour
import evoshift.composeapp.generated.resources.job_pay_per_month
import org.jetbrains.compose.resources.StringResource
import org.jetbrains.compose.resources.stringResource

/**
 * SH-125: Team → Hiring: the workplace's job posts, each with its stage and how many applied (and
 * how many are new). A post opens its applicants; "Edit the post" its editor; the button at the
 * bottom writes a new one (managers who may change the workplace).
 */
@Composable
fun HiringScreen(
    viewModel: HiringViewModel,
    onNavigateBack: () -> Unit,
    onOpenApplicants: (postId: String) -> Unit,
    onEditPost: (postId: String?) -> Unit
) {
    val state by viewModel.uiState.collectAsStateWithLifecycle()
    val snackbarHostState = remember { SnackbarHostState() }
    val messageText = state.message?.asString()
    LaunchedEffect(messageText) {
        messageText?.let {
            snackbarHostState.showSnackbar(it)
            viewModel.messageShown()
        }
    }

    Scaffold(
        containerColor = Cream,
        snackbarHost = { SnackbarHost(snackbarHostState) },
        topBar = { EvoTopBar(title = stringResource(Res.string.hiring_title), onBack = onNavigateBack, showWorkplace = true) },
        floatingActionButton = {
            if (state.canEdit) {
                ExtendedFloatingActionButton(
                    onClick = { onEditPost(null) },
                    containerColor = DeepNavy,
                    contentColor = Color.White,
                    icon = { Icon(Icons.Default.Add, contentDescription = null) },
                    text = { Text(stringResource(Res.string.hiring_new_post)) },
                    modifier = Modifier.testTag(HiringTags.NEW)
                )
            }
        }
    ) { padding ->
        Box(Modifier.fillMaxSize().contentWidth(LocalWindowWidthClass.current.wideContentMaxWidth).padding(padding)) {
            AdaptiveCardGrid(
                modifier = Modifier.fillMaxSize().padding(horizontal = 16.dp),
                spacing = 16.dp,
                contentPadding = PaddingValues(top = 16.dp, bottom = 96.dp)
            ) {
                if (state.posts.isEmpty() && !state.isLoading) {
                    item(span = { GridItemSpan(maxLineSpan) }) {
                        Text(
                            stringResource(if (state.isRecruiterOnly) Res.string.hiring_recruiter_empty else Res.string.hiring_empty),
                            color = DeepNavy.copy(alpha = 0.6f)
                        )
                    }
                }
                items(state.posts, key = { it.id }) { post ->
                    Box(Modifier.animateItemIfMotion(this), propagateMinConstraints = true) {
                        JobPostCard(
                            post = post,
                            unread = state.unreadOf(post),
                            canEdit = state.capabilities.shows(Gate.HIRING_EDITING),
                            onOpen = { onOpenApplicants(post.id) },
                            onEdit = { onEditPost(post.id) }
                        )
                    }
                }
            }
            LoadingOverlay(isLoading = state.isLoading && state.posts.isEmpty())
        }
    }
}

object HiringTags {
    const val NEW = "hiring_new_post"
}

/** The label of a post's stage. */
fun JobPostStatus.label(): StringResource = when (this) {
    JobPostStatus.DRAFT -> Res.string.hiring_status_draft
    JobPostStatus.OPEN -> Res.string.hiring_status_open
    JobPostStatus.CLOSED -> Res.string.hiring_status_closed
}

/** "30–35 PLN per hour". */
@Composable
fun payLine(post: JobPost): String {
    fun amount(v: Double) = if (v % 1.0 == 0.0) v.toLong().toString() else v.toString()
    val range = if (post.payMin == post.payMax) amount(post.payMin) else "${amount(post.payMin)}–${amount(post.payMax)}"
    val unit = stringResource(if (post.payUnit == PayUnit.MONTH) Res.string.job_pay_per_month else Res.string.job_pay_per_hour)
    return "$range ${post.currency} · $unit"
}

@Composable
private fun JobPostCard(post: JobPost, unread: Int, canEdit: Boolean, onOpen: () -> Unit, onEdit: () -> Unit) {
    RegularCard(modifier = Modifier.fillMaxWidth(), onClick = onOpen) {
        Column(Modifier.padding(16.dp), verticalArrangement = Arrangement.spacedBy(6.dp)) {
            Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                Text(post.title, style = MaterialTheme.typography.titleMedium, fontWeight = FontWeight.Bold, color = DeepNavy, modifier = Modifier.weight(1f))
                Text(stringResource(post.status.label()), style = MaterialTheme.typography.labelLarge, color = DarkGold, fontWeight = FontWeight.Bold)
            }
            Text(payLine(post), style = MaterialTheme.typography.bodyMedium, color = DeepNavy.copy(alpha = 0.8f))
            Text(
                stringResource(Res.string.hiring_applicants_count, post.totalApplicants, post.newApplicants),
                style = MaterialTheme.typography.bodyMedium,
                color = DeepNavy
            )
            // SH-210: the viewer's own unread applications.
            Text(
                stringResource(Res.string.hiring_unread_count, unread),
                style = MaterialTheme.typography.bodyMedium,
                color = if (unread > 0) DarkGold else DeepNavy.copy(alpha = 0.7f),
                fontWeight = if (unread > 0) FontWeight.Bold else FontWeight.Normal
            )
            if (canEdit) {
                TextButton(onClick = onEdit, colors = ButtonDefaults.textButtonColors(contentColor = DarkGold)) {
                    Icon(Icons.Default.Edit, contentDescription = null, modifier = Modifier.padding(end = 4.dp))
                    Text(stringResource(Res.string.hiring_edit))
                }
            }
        }
    }
}
